import { useState } from "react";
import { useLocation } from "wouter";
import {
  CalendarDays,
  ShieldCheck,
  Bell,
  Plug,
  User,
  Database,
  Building2,
  Activity,
  LogOut, Lock } from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";
import { useI18n } from "@/lib/i18n";
import { trpc } from "@/lib/trpc";
import { TabScaffold, mergeTabQueries } from "@/components/tabs/TabScaffold";
import { AppTopBar } from "@/components/home/AppTopBar";
import { MoreSection, MoreRow } from "@/components/more/MoreSection";
import { AccountDataSheet } from "@/components/more/AccountDataSheet";
import {
  SettingsDialog,
  showTeamSharingSettings,
  type SettingsSection,
} from "@/components/SettingsDialog";
import { IntegrationsDialog } from "@/components/IntegrationsDialog";
import { useActiveWorkspace } from "@/contexts/ActiveWorkspace";

/**
 * More tab — a thin settings HUB. It reuses the existing settings surfaces
 * (`SettingsDialog`, `IntegrationsDialog`, `Profile`, admin pages) and existing
 * endpoints; it computes NO sensitive value and duplicates NO preference /
 * work-week / sharing logic. Admin rows are hidden unless `org.me.isAdmin`, but
 * that is cosmetic — the admin endpoints are authorized server-side. Additive at
 * `/more`; Canvas `/` is untouched.
 */
export default function More() {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const { isAuthenticated, logout } = useAuth();
  const [, navigate] = useLocation();

  const prefs = trpc.preferences.me.useQuery(undefined, { enabled: isAuthenticated });
  const org = trpc.org.me.useQuery(undefined, { enabled: isAuthenticated });
  const state = mergeTabQueries({ prefs, org });
  // The "Sharing & privacy" row deep-links to the settings sharing section,
  // which only exists for users with a team workspace. Hide the row for a
  // no-team (personal-only) user so it never points to a hidden section.
  const showTeamSharing = showTeamSharingSettings(useActiveWorkspace());

  const [settingsOpen, setSettingsOpen] = useState(false);
  // Which section the settings dialog should land on — set by the row the
  // user tapped, so "Notifications" actually opens AT Notifications instead
  // of dumping them at the top of a long dialog.
  const [settingsSection, setSettingsSection] = useState<SettingsSection | undefined>();
  const openSettingsAt = (section?: SettingsSection) => {
    setSettingsSection(section);
    setSettingsOpen(true);
  };
  const [integrationsOpen, setIntegrationsOpen] = useState(false);
  const [dataOpen, setDataOpen] = useState(false);

  return (
    <>
      <TabScaffold title={isAr ? "المزيد" : "More"} topBar={<AppTopBar />} state={state}>
        {d => {
          const tz = (d.prefs as any)?.timezone as string | undefined;
          // Model-2 UI boundary: show the ADMIN section only when the
          // caller can MANAGE a real organization — NOT merely when they're
          // an org admin. A solo personal user is technically the owner
          // (hence admin) of their auto-created org shell, but that shell
          // has no organization intent, so no company-admin chrome appears.
          // `canManageOrg` is provenance/structure-based and stays true for
          // a real org admin even while they view their Personal workspace.
          const canManageOrg = !!(d.org as any)?.canManageOrg;
          const isPlatform = !!(d.org as any)?.platformAdmin;
          const orgName = (d.org as any)?.name as string | undefined;
          return (
            <div className="space-y-4">
              {/* Phase 2A — secondary entry to Your Week (primary is a Home card).
                  Deliberately not a fifth bottom tab. */}
              <MoreSection title={isAr ? "أسبوعك" : "YOUR WEEK"}>
                <MoreRow
                  icon={Lock}
                  label={isAr ? "أسبوعك" : "Your Week"}
                  sublabel={isAr ? "مرئية لك وحدك" : "Visible only to you"}
                  onClick={() => navigate("/your-week")}
                  testId="row-your-week"
                />
              </MoreSection>

              <MoreSection title={isAr ? "التفضيلات" : "PREFERENCES"}>
                <MoreRow
                  icon={CalendarDays}
                  label={isAr ? "أسبوع العمل والمنطقة" : "Work week & region"}
                  sublabel={
                    tz
                      ? isAr
                        ? `أيام العمل، بداية الأسبوع · ${tz}`
                        : `Working days, week start · ${tz}`
                      : isAr
                        ? "أيام العمل، بداية الأسبوع، المنطقة الزمنية"
                        : "Working days, week start, timezone"
                  }
                  onClick={() => openSettingsAt("workweek")}
                  testId="row-workweek"
                />
                {showTeamSharing ? (
                  <MoreRow
                    icon={ShieldCheck}
                    label={isAr ? "المشاركة والخصوصية" : "Sharing & privacy"}
                    sublabel={
                      isAr
                        ? "تفاصيل الملخّص، ظهور المعوقات، الاعتماد/المشاركة"
                        : "Receipt detail, blocker visibility, approval/share"
                    }
                    onClick={() => openSettingsAt("privacy")}
                    testId="row-privacy"
                  />
                ) : null}
                <MoreRow
                  icon={Bell}
                  label={isAr ? "الإشعارات" : "Notifications"}
                  sublabel={isAr ? "القنوات والفئات" : "Channels and categories"}
                  onClick={() => openSettingsAt("notifications")}
                  testId="row-notifications"
                />
              </MoreSection>

              <MoreSection title={isAr ? "الاتصالات" : "CONNECTIONS"}>
                <MoreRow
                  icon={Plug}
                  label={isAr ? "التكاملات" : "Integrations"}
                  sublabel={isAr ? "GitHub وSlack والتقويم والمزيد" : "GitHub, Slack, calendar and more"}
                  onClick={() => setIntegrationsOpen(true)}
                  testId="row-integrations"
                />
              </MoreSection>

              <MoreSection title={isAr ? "الحساب" : "ACCOUNT"}>
                <MoreRow
                  icon={User}
                  label={isAr ? "الملف الشخصي" : "Profile"}
                  sublabel={isAr ? "حسابك وسجلّ ملخّصاتك" : "Your account and receipt history"}
                  onClick={() => navigate("/me")}
                  testId="row-profile"
                />
                <MoreRow
                  icon={Database}
                  label={isAr ? "تصدير البيانات أو حذفها" : "Export or delete data"}
                  sublabel={isAr ? "نزّل بياناتك أو احذف حسابك" : "Download your data, or delete your account"}
                  onClick={() => setDataOpen(true)}
                  testId="row-data"
                />
                <MoreRow
                  icon={LogOut}
                  label={isAr ? "تسجيل الخروج" : "Sign out"}
                  onClick={() => logout()}
                  testId="row-signout"
                  right={<span />}
                />
              </MoreSection>

              {/* Build stamp — lets anyone verify by eye that their device
                  runs the latest deploy (stale WebView/PWA cache detector). */}
              <div className="px-1 text-center text-[10px] text-muted-foreground/60">
                Build {typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : "dev"}
              </div>

              {canManageOrg ? (
                <MoreSection
                  title={isAr ? "الإدارة" : "ADMIN"}
                  // Name the organization being administered, so it's never
                  // ambiguous that this is COMPANY admin, not the Personal
                  // workspace (which may be the active tab).
                  subtitle={
                    orgName
                      ? isAr
                        ? `إدارة مؤسسة: ${orgName}`
                        : `Administering: ${orgName}`
                      : undefined
                  }
                >
                  <MoreRow
                    icon={Building2}
                    label={isAr ? "الفرق والأعضاء" : "Teams & members"}
                    sublabel={isAr ? "إدارة فرق مؤسستك" : "Manage your organization’s teams"}
                    onClick={() => navigate("/admin/teams")}
                    testId="row-admin-teams"
                  />
                  <MoreRow
                    icon={Activity}
                    label={isAr ? "صحة المؤسسة والاحتفاظ بالبيانات" : "Org health & retention"}
                    sublabel={isAr ? "سجل التدقيق والاحتفاظ بالبيانات" : "Audit log and data retention"}
                    onClick={() => navigate("/admin/health")}
                    testId="row-admin-health"
                  />
                  {isPlatform ? (
                    <MoreRow
                      icon={Activity}
                      label={isAr ? "وحدة تحكم المنصة" : "Platform console"}
                      onClick={() => navigate("/platform")}
                      testId="row-platform"
                    />
                  ) : null}
                  {/* Platform-tier health. Named "Platform health" rather than
                      "Health" because the row directly above is ORG health, and
                      two rows both reading "health" would be a coin flip. */}
                  {isPlatform ? (
                    <MoreRow
                      icon={Activity}
                      label={isAr ? "صحة المنصّة" : "Platform health"}
                      sublabel={
                        isAr
                          ? "حالة النظام والذكاء الاصطناعي وقاعدة البيانات"
                          : "System, AI and database status"
                      }
                      onClick={() => navigate("/platform/health")}
                      testId="row-platform-health"
                    />
                  ) : null}
                </MoreSection>
              ) : null}
            </div>
          );
        }}
      </TabScaffold>

      {/* Reused settings surfaces (unchanged). */}
      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        initialSection={settingsSection}
      />
      <IntegrationsDialog open={integrationsOpen} onOpenChange={setIntegrationsOpen} />
      <AccountDataSheet open={dataOpen} onOpenChange={setDataOpen} />
    </>
  );
}
